<template>
  <div class="addon-services">
    <h3 class="section-title">Dịch vụ bổ sung</h3>
    <p class="addons-subtitle">Mua và nhận trong chuyến đi</p>

    <!-- Loading State -->
    <div v-if="isLoading" class="addon-loading-state">
      <div class="spinner"></div>
      <p>Đang tải dịch vụ bổ sung...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="addon-error-state">
      <i class="fas fa-exclamation-circle"></i>
      <p>Không có dịch vụ bổ sung nào cho chuyến đi này.</p>
      <!-- <button @click="fetchAddonProducts" class="retry-button">Thử lại</button> -->
    </div>

    <!-- Empty State -->
    <div v-else-if="addonProducts.length === 0" class="no-addons-available">
      <p>Hiện không có dịch vụ bổ sung nào cho chuyến đi này.</p>
    </div>

    <!-- Products Grid with Carousel -->
    <div v-else class="addon-carousel-container">
      <!-- Navigation Buttons -->
      <button 
        class="carousel-nav-btn prev-btn" 
        @click="scrollCarousel('left')"
        :disabled="!canScrollLeft"
        v-show="canScrollLeft || canScrollRight"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="addon-products-grid" ref="scrollContainer" @scroll="checkScroll">
        <div
          v-for="product in addonProducts"
          :key="product.addon_id"
          class="addon-product-card"
        >
          <!-- Phần có thể click để mở detail -->
          <div
            class="addon-product-clickable"
            @click="openProductDetail(product)"
          >
            <div class="addon-product-image">
              <div class="image-container">
                <img
                  v-if="getProductMainImage(product)"
                  :src="getProductMainImage(product)"
                  :alt="product.name"
                  class="product-image"
                />
                <!-- Placeholder khi không có ảnh -->
                <div v-else class="image-placeholder">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 16V7.2C21 6.0799 21 5.51984 20.782 5.09202C20.5903 4.71569 20.2843 4.40973 19.908 4.21799C19.4802 4 18.9201 4 17.8 4H6.2C5.07989 4 4.51984 4 4.09202 4.21799C3.71569 4.40973 3.40973 4.71569 3.21799 5.09202C3 5.51984 3 6.0799 3 7.2V16M21 16C21 17.8856 21 18.8284 20.4142 19.4142C19.8284 20 18.8856 20 17 20H7C5.11438 20 4.17157 20 3.58579 19.4142C3 18.8284 3 17.8856 3 16M21 16H3M6 8H7.5M6 12H10"
                      stroke="#718096"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ product.name }}</span>
                </div>

                <!-- Badge số lượng ảnh -->
                <div
                  v-if="product.images && product.images.length > 1"
                  class="image-count-badge"
                >
                  <i class="fas fa-images"></i>
                  {{ product.images.length }}
                </div>
              </div>
            </div>

            <div class="addon-product-info">
              <h4 class="addon-product-name">{{ product.name }}</h4>
              <p class="addon-product-description">{{ product.description }}</p>
              <div class="addon-product-price">
                {{ formatCurrency(product.price) }}
              </div>

              <!-- Hiển thị stock info -->
              <div class="stock-info">
                <div v-if="product.available_quantity > 10" class="stock-good">
                  <i class="fas fa-check-circle"></i>
                  Còn hàng
                </div>
                <!-- Simplified stock logic here for brevity if needed, keeping mostly same -->
                 <div
                  v-else-if="product.available_quantity > 0"
                  class="stock-limited"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                  Chỉ còn {{ product.available_quantity }}
                </div>
                <div v-else class="stock-out">
                  <i class="fas fa-times-circle"></i>
                  Hết hàng
                </div>
              </div>
            </div>
          </div>

          <!-- Phần quantity controls -->
          <div class="addon-product-actions" @click.stop>
            <div class="quantity-controls" v-if="product.available_quantity > 0">
              <button
                type="button"
                class="quantity-btn"
                @click.stop="decreaseAddonQuantity(product.addon_id)"
                :disabled="getAddonQuantity(product.addon_id) <= 0"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 12L18 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
              <div class="quantity-display">
                {{ getAddonQuantity(product.addon_id) }}
              </div>
              <button
                type="button"
                class="quantity-btn"
                @click.stop="increaseAddonQuantity(product.addon_id)"
                :disabled="getAddonQuantity(product.addon_id) >= (product.max_can_add || 0)"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M4 12H20M12 4V20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="out-of-stock-message" v-else>
              <span>Tạm hết hàng</span>
            </div>
          </div>
        </div>
      </div>

      <button 
        class="carousel-nav-btn next-btn" 
        @click="scrollCarousel('right')"
        :disabled="!canScrollRight"
        v-show="canScrollLeft || canScrollRight"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Optional: No products selected info -->
    <div
      v-if="
        !isLoading &&
        !hasError &&
        addonProducts.length > 0 &&
        selectedAddons.length === 0
      "
      class="no-addons-selected"
    >
      <p>Bạn chưa chọn thêm dịch vụ nào. Dịch vụ bổ sung không bắt buộc.</p>
    </div>

    <!-- Selected Addons Summary -->
    <div
      v-if="!isLoading && !hasError && selectedAddons.length > 0"
      class="selected-addons-summary"
    >
      <h4>Dịch vụ đã chọn</h4>
      <ul class="selected-addons-list">
        <li
          v-for="addon in selectedAddons"
          :key="addon.productId"
          class="selected-addon-item"
        >
          <span class="selected-addon-name">{{
            getAddonName(addon.productId)
          }}</span>
          <span class="selected-addon-quantity">x{{ addon.quantity }}</span>
          <span class="selected-addon-price">{{
            formatCurrency(getAddonPrice(addon.productId) * addon.quantity)
          }}</span>
        </li>
      </ul>
      <div class="addons-total">
        <span>Tổng phụ thu:</span>
        <span class="addons-total-amount">{{
          formatCurrency(calculateAddonsTotal)
        }}</span>
      </div>
    </div>

    <!-- Updated Booking Summary including addons -->
    <div class="booking-summary" v-if="trip && !isLoading && !hasError">
      <div class="selected-info">
        <div class="route" v-if="destination">{{ destination }}</div>
        <div class="time" v-if="trip">
          {{ formatDateTime(trip.departure_datetime) }}
        </div>
        <div class="ticket-type" v-if="ticketTypeName">
          Loại vé: {{ ticketTypeName }}
        </div>
        <div class="seats" v-if="!isSpecialTicket">
          Ghế đã chọn: {{ selectedSeats.join(', ') }}
        </div>
        <div class="group-info">
          Số hành khách:
          {{ isSpecialTicket ? specialTicketQuantity : selectedSeats.length }}
        </div>
        <div class="addons-info" v-if="selectedAddons.length > 0">
          <span>Dịch vụ bổ sung: {{ selectedAddons.length }} loại</span>
        </div>
      </div>
      <div class="total-price">
        <span class="amount">{{ formatCurrency(totalWithAddons) }}</span>
      </div>
    </div>

    <!-- Product Detail Modal -->
    <div
      class="modal"
      v-if="showProductDetail"
      @click.self="closeProductDetail"
    >
      <div class="modal-content product-detail-modal">
        <div class="modal-header">
          <h2>{{ selectedProduct?.name }}</h2>
          <button class="close-btn" @click="closeProductDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="product-detail-content">
            <div class="product-images">
              <div class="main-image">
                <img
                  :src="selectedProductMainImage"
                  :alt="selectedProduct?.name"
                />
              </div>
              <div
                v-if="
                  selectedProduct?.images && selectedProduct.images.length > 1
                "
                class="image-thumbnails"
              >
                <div
                  v-for="(image, index) in selectedProduct.images"
                  :key="index"
                  class="thumbnail"
                  :class="{ active: selectedImageIndex === index }"
                  @click="selectedImageIndex = index"
                >
                  <img :src="image.image_url" :alt="`Image ${index + 1}`" />
                </div>
              </div>
            </div>
            <div class="product-info">
              <h3>{{ selectedProduct?.name }}</h3>
              <p class="price">{{ formatCurrency(selectedProduct?.price) }}</p>
              <p class="description">{{ selectedProduct?.description }}</p>

              <!-- Quantity controls trong modal -->
              <div class="quantity-section">
                <label>Số lượng:</label>
                <div class="quantity-controls">
                  <button
                    type="button"
                    class="quantity-btn"
                    @click="decreaseAddonQuantity(selectedProduct.addon_id)"
                    :disabled="getAddonQuantity(selectedProduct.addon_id) <= 0"
                  >
                    <svg
                      data-v-ac898e02=""
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        data-v-ac898e02=""
                        d="M6 12L18 12"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  </button>
                  <div class="quantity-display">
                    {{ getAddonQuantity(selectedProduct.addon_id) }}
                  </div>
                  <button
                    type="button"
                    class="quantity-btn"
                    @click="
                      increaseAddonQuantity(
                        selectedProduct.addon_id,
                        selectedProduct.max_quantity
                      )
                    "
                    :disabled="
                      getAddonQuantity(selectedProduct.addon_id) >=
                      getMaxAllowedQuantity(selectedProduct.addon_id)
                    "
                  >
                    <svg
                      data-v-27d10387=""
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        data-v-27d10387=""
                        d="M4 12H20M12 4V20"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axiosInstance from '@/plugins/axios'
import { sharedVueCheckout } from '@/services/checkoutV2Flow'
import { CHECKOUT_V2_ENABLED } from '@/services/checkoutV2Flag'
import { ToastService } from '@/services/ToastService'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AddonServices',

  props: {
    trip: {
      type: Object,
      required: true
    },
    destination: {
      type: String,
      required: true
    },
    ticketTypeName: {
      type: String,
      required: true
    },
    selectedSeats: {
      type: Array,
      required: true
    },
    isSpecialTicket: {
      type: Boolean,
      default: false
    },
    specialTicketQuantity: {
      type: Number,
      default: 1
    },
    baseTotalPrice: {
      type: Number,
      required: true
    },
    customerEmail: {
      type: String,
      default: ''
    },
    customerPhone: {
      type: String,
      default: ''
    }
  },

  emits: ['update:selectedAddons'],

  setup (props, { emit }) {
    const addonProducts = ref([])
    const selectedAddons = ref([])
    const isLoading = ref(true)
    const hasError = ref(false)
    const showProductDetail = ref(false)
    const selectedProduct = ref(null)
    const selectedImageIndex = ref(0)
    const sessionId = ref(generateSessionId())
    const stockRefreshInterval = ref(null)
    const countdownInterval = ref(null)
    // Removed debounceTimers - now only syncing on page navigation

    function generateSessionId () {
      return (
        'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      )
    }

    // Tải danh sách sản phẩm phụ trợ từ API
    const fetchAddonProducts = async () => {
      try {
        isLoading.value = true
        hasError.value = false

        const response = await axiosInstance.get(
          `${API_BASE_URL}/addon-products/`
        )

        // Chuẩn hóa dữ liệu từ API để đảm bảo max_quantity tồn tại
        addonProducts.value = response.data.map(product => ({
          ...product,
          max_quantity: product.max_quantity || product.maxQuantity || 10,
          available_quantity: 0, // Khởi tạo với 0, sẽ update sau
          total_stock: product.max_quantity || 10,
          reserved_quantity: 0
        }))

        await loadAllStockInfo()
      } catch (error) {
        console.error('Error fetching addon products:', error)
        hasError.value = true
      } finally {
        isLoading.value = false
      }
    }

    const loadAllStockInfo = async () => {
      const stockPromises = addonProducts.value.map(async product => {
        try {
          const response = await axiosInstance.get(
            `${API_BASE_URL}/addon-products/${product.addon_id}/availability?session_id=${sessionId.value}`
          )

          // Cập nhật stock info cho sản phẩm
          product.available_quantity = response.data.available_quantity
          product.total_stock = response.data.total_stock
          product.reserved_quantity = response.data.reserved_quantity
          product.user_reserved = response.data.user_reserved
          product.max_can_add = response.data.max_can_add
        } catch (error) {
          console.error(
            `Error loading stock for product ${product.addon_id}:`,
            error
          )
          // Fallback values
          product.available_quantity = product.max_quantity
          product.max_can_add = product.max_quantity
        }
      })

      await Promise.all(stockPromises)
    }

    // Tính tổng giá trị các sản phẩm phụ trợ
    const calculateAddonsTotal = computed(() => {
      return selectedAddons.value.reduce((total, addon) => {
        const product = addonProducts.value.find(
          p => p.addon_id === addon.productId
        )
        return total + (product ? product.price * addon.quantity : 0)
      }, 0)
    })

    // Tổng giá vé + sản phẩm phụ trợ
    const totalWithAddons = computed(() => {
      return props.baseTotalPrice + calculateAddonsTotal.value
    })

    const formatCurrency = amount => {
      const num = Number(amount)
      return num.toLocaleString('vi-VN') + 'đ'
    }

    const formatDateTime = datetime => {
      const date = new Date(datetime)
      return date.toLocaleDateString('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Lấy số lượng của một sản phẩm
    const getAddonQuantity = productId => {
      const addon = selectedAddons.value.find(a => a.productId === productId)
      return addon ? addon.quantity : 0
    }

    // Lấy tên sản phẩm
    const getAddonName = productId => {
      const product = addonProducts.value.find(p => p.addon_id === productId)
      return product ? product.name : ''
    }

    // Lấy giá sản phẩm
    const getAddonPrice = productId => {
      const product = addonProducts.value.find(p => p.addon_id === productId)
      return product ? product.price : 0
    }


    const increaseAddonQuantity = productId => {
      const product = addonProducts.value.find(p => p.addon_id === productId)
      if (!product) return

      const existingAddon = selectedAddons.value.find(a => a.productId === productId)
      const currentQuantity = existingAddon ? existingAddon.quantity : 0
      const newQuantity = currentQuantity + 1

      // Local validation
      if (newQuantity > (product.max_can_add || product.max_quantity || 10)) {
        ToastService.error(`Chỉ còn ${product.max_can_add || product.max_quantity} sản phẩm`)
        return
      }

      // Update local state only
      if (existingAddon) {
        existingAddon.quantity = newQuantity
      } else {
        selectedAddons.value.push({
          productId,
          quantity: newQuantity,
          price: product.price,
          name: product.name
        })
      }

      emit('update:selectedAddons', [...selectedAddons.value])
    }

    const decreaseAddonQuantity = productId => {
      const existingAddon = selectedAddons.value.find(a => a.productId === productId)
      if (!existingAddon || existingAddon.quantity <= 0) return

      const newQuantity = existingAddon.quantity - 1

      // Update local state only
      if (newQuantity === 0) {
        const idx = selectedAddons.value.findIndex(a => a.productId === productId)
        if (idx > -1) selectedAddons.value.splice(idx, 1)
      } else {
        existingAddon.quantity = newQuantity
      }

      emit('update:selectedAddons', [...selectedAddons.value])
    }

    // Reserve all addons at once (call this when navigating to next page)
    const vueCheckout = sharedVueCheckout()

    const reserveAllAddons = async () => {
      if (CHECKOUT_V2_ENABLED) {
        // Hardened path: the cart is part of the owned selection, written with the seats and
        // the current revision. One call, one transaction, no per-product reservations.
        try {
          const cart = selectedAddons.value.map(addon => ({ addon_id: addon.productId, quantity: addon.quantity }))
          await vueCheckout.chooseAddons(cart)
          return true
        } catch (error) {
          ToastService.error(error?.message || 'Không giữ được dịch vụ. Vui lòng thử lại.')
          return false
        }
      }
      const reservePromises = selectedAddons.value.map(async addon => {
        try {
          const response = await axiosInstance.post(
            `${API_BASE_URL}/addon-products/reserve-stock`,
            {
              addon_id: addon.productId,
              quantity: addon.quantity,
              session_id: sessionId.value,
              user_identifier: props.customerEmail || props.customerPhone
            }
          )
          addon.reservationId = response.data.reservation_id
          addon.expiresAt = response.data.expires_at
          return { success: true, productId: addon.productId }
        } catch (error) {
          console.error(`Error reserving ${addon.name}:`, error)
          return { 
            success: false, 
            productId: addon.productId, 
            error: error.response?.data?.detail || 'Không thể đặt hàng'
          }
        }
      })

      const results = await Promise.all(reservePromises)
      const failures = results.filter(r => !r.success)
      
      if (failures.length > 0) {
        // Remove failed items from selection
        failures.forEach(f => {
          const idx = selectedAddons.value.findIndex(a => a.productId === f.productId)
          if (idx > -1) selectedAddons.value.splice(idx, 1)
          ToastService.error(f.error)
        })
        emit('update:selectedAddons', [...selectedAddons.value])
        // Refresh stock info for failed items
        await loadAllStockInfo()
        return false
      }
      
      return true
    }

    // Refresh stock info
    const refreshStockInfo = async (productId) => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/addon-products/${productId}/availability?session_id=${sessionId.value}`
        )
        
        const product = addonProducts.value.find(p => p.addon_id === productId)
        if (product) {
          product.available_quantity = response.data.available_quantity
          product.total_stock = response.data.total_stock
          product.reserved_quantity = response.data.reserved_quantity
          product.user_reserved = response.data.user_reserved
          product.max_can_add = response.data.max_can_add
        }
      } catch (error) {
        console.error('Lỗi khi refresh stock info:', error)
      }
    }

    const startStockRefreshInterval = () => {
      if (stockRefreshInterval.value) {
        clearInterval(stockRefreshInterval.value)
      }

      stockRefreshInterval.value = setInterval(async () => {
        const productsToRefresh = addonProducts.value.filter(product => {
          const inCart = getAddonQuantity(product.addon_id) > 0
          const lowStock = product.available_quantity <= 5
          return inCart || lowStock
        })

        for (const product of productsToRefresh) {
          await refreshStockInfo(product.addon_id)
        }
      }, 30000)
    }

    // Đếm ngược thời gian hết hạn reservation
    const startCountdown = () => {
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value)
      }

      countdownInterval.value = setInterval(() => {
        const now = new Date()
        selectedAddons.value.forEach(addon => {
          if (addon.expiresAt) {
            const expiresAt = new Date(addon.expiresAt)
            const timeLeft = expiresAt - now

            if (timeLeft <= 0) {
              ToastService.warning(`Reservation cho "${addon.name}" đã hết hạn`)
            }
          }
        })
      }, 5000)
    }

    // Confirm reservation khi thanh toán thành công
    const confirmReservations = async bookingId => {
      try {
        await axiosInstance.post(
          `${API_BASE_URL}/addon-products/confirm-reservation`,
          null,
          {
            params: {
              session_id: sessionId.value,
              booking_id: bookingId
            }
          }
        )
      } catch (error) {
        console.error('Lỗi khi confirm reservations:', error)
        throw error
      }
    }

    // Hủy reservation khi user rời trang
    const cancelReservations = async () => {
      try {
        await axiosInstance.delete(
          `${API_BASE_URL}/addon-products/cancel-reservation/${sessionId.value}`
        )
      } catch (error) {
        console.error('Lỗi khi cancel reservations:', error)
      }
    }

    // Cleanup khi component unmount
    onBeforeUnmount(() => {
      if (stockRefreshInterval.value) {
        clearInterval(stockRefreshInterval.value)
        stockRefreshInterval.value = null
      }
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value)
        countdownInterval.value = null
      }
      cancelReservations()
    })

    // Start countdown timer
    onMounted(() => {
      fetchAddonProducts()
      startCountdown()
      startStockRefreshInterval()
    })

    // Thêm hàm getMaxAllowedQuantity bị thiếu
    const getMaxAllowedQuantity = (productId) => {
      const product = addonProducts.value.find(p => p.addon_id === productId)
      if (!product) return 0
      
      // Sử dụng max_can_add từ stock availability thay vì max_quantity
      return product.max_can_add || 0
    }


    // Thêm các hàm bị thiếu khác
    const getProductMainImage = product => {
      if (product.images && product.images.length > 0) {
        return product.images[0].image_url
      }
      return product.image_url || null
    }

    const selectedProductMainImage = computed(() => {
      if (!selectedProduct.value) return null
      if (
        selectedProduct.value.images &&
        selectedProduct.value.images.length > 0
      ) {
        return selectedProduct.value.images[selectedImageIndex.value]?.image_url
      }
      return selectedProduct.value.image_url || null
    })

    const openProductDetail = product => {
      selectedProduct.value = product
      selectedImageIndex.value = 0
      showProductDetail.value = true
      showProductDetail.value = true
    }

    // Carousel logic
    const scrollContainer = ref(null)
    const canScrollLeft = ref(false)
    const canScrollRight = ref(true) // Assume initially true if content exists

    const checkScroll = () => {
      const el = scrollContainer.value
      if (el) {
        canScrollLeft.value = el.scrollLeft > 0
        // Tolerance for checking if scrolled to end
        canScrollRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth
      }
    }

    const scrollCarousel = (direction) => {
      const el = scrollContainer.value
      if (el) {
        const scrollAmount = 240 // Card width + gap
        const targetScroll = direction === 'left' 
          ? el.scrollLeft - scrollAmount 
          : el.scrollLeft + scrollAmount
        
        el.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        })
      }
    }

    onMounted(() => {
      // Check scroll status initially
      setTimeout(checkScroll, 500)
      window.addEventListener('resize', checkScroll)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkScroll)
    })

    const closeProductDetail = () => {
      showProductDetail.value = false
      selectedProduct.value = null
      selectedImageIndex.value = 0
    }

    const getTimeLeft = productId => {
      const addon = selectedAddons.value.find(a => a.productId === productId)
      if (!addon || !addon.expiresAt) return ''

      const now = new Date()
      const expiresAt = new Date(addon.expiresAt)
      const timeLeft = expiresAt - now

      if (timeLeft <= 0) return 'Đã hết hạn'

      const minutes = Math.floor(timeLeft / 60000)
      const seconds = Math.floor((timeLeft % 60000) / 1000)

      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }

    return {
      addonProducts,
      selectedAddons,
      isLoading,
      hasError,
      calculateAddonsTotal,
      totalWithAddons,
      formatCurrency,
      formatDateTime,
      getAddonQuantity,
      getAddonName,
      getAddonPrice,
      increaseAddonQuantity,
      decreaseAddonQuantity,
      getMaxAllowedQuantity,
      fetchAddonProducts,
      showProductDetail,
      selectedProduct,
      selectedImageIndex,
      selectedProductMainImage,
      getProductMainImage,
      openProductDetail,
      closeProductDetail,
      sessionId,
      confirmReservations,
      cancelReservations,
      refreshStockInfo,
      getTimeLeft,
      scrollContainer,
      canScrollLeft,
      canScrollRight,
      scrollCarousel,
      checkScroll,
      reserveAllAddons
    }
  }
}
</script>

<style lang="scss" scoped>
/* Thêm các styles mới này */
.addon-loading-state,
.addon-error-state,
.no-addons-available {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  text-align: center;
  background: #f9fafb;
  border-radius: 0.75rem;
  border: 1px dashed #e2e8f0;
  margin-bottom: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #0284c7;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.addon-error-state i {
  font-size: 2rem;
  color: #e53e3e;
  margin-bottom: 1rem;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  background-color: #0284c7;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background-color: #2c5282;
}

/* Giữ các styles hiện tại */
.addon-services {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 24px;
  margin-bottom: 20px;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  bottom: -5px;
  left: 0;
  background: linear-gradient(to right, #007bff, #6610f2);
}

/* Carousel Container Layout */
.addon-carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 -12px; /* Negative margin to offset padding if needed, or adjust */
  padding: 0 12px;
}

.addon-products-grid {
  display: flex;
  overflow-x: auto;
  gap: 20px; /* Increased gap */
  padding: 12px 4px 24px 4px; /* Space for shadow */
  scroll-behavior: smooth;
  scrollbar-width: none; /* Hide scrollbar for cleaner look */
  width: 100%;
}

.addon-products-grid::-webkit-scrollbar {
  display: none; /* Hide scrollbar */
}

/* Navigation Buttons */
.carousel-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #075985;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #fff;
    color: #0284c7;
    box-shadow: 0 6px 12px rgba(0,0,0,0.15);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.prev-btn {
  left: -20px; /* Pull button outside slightly */
}

.next-btn {
  right: -20px;
}

/* Responsive button placement */
@media (max-width: 768px) {
  .carousel-nav-btn {
    display: none; /* Hide nav buttons on mobile relying on touch */
  }
  .addon-carousel-container {
    margin: 0;
    padding: 0;
  }
}

.addon-product-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  
  /* Fixed dimensions for consistency - Increased size */
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  height: 320px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
    border-color: #cbd5e0;
  }
}

.addon-product-image {
  width: 100%;
  height: 140px; /* Increased height */
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
  color: #718096;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 0.75rem;
    opacity: 0.7;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    text-align: center;
    padding: 0 1rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* Hiệu ứng hover trên ảnh */
.addon-product-card:hover .product-image {
  transform: scale(1.05);
}

.addon-product-info {
  padding: 16px; /* Increased padding */
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.addon-product-name {
  font-size: 15px; /* Slightly larger font */
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 42px; /* Adjusted min-height */
}

.addon-product-description {
  font-size: 13px;
  color: #666;
  margin: 0 0 10px 0;
  line-height: 1.4;
  /* Hide description on desktop to save space */
  display: none; 
}

.addon-product-price {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #059669;
  margin-top: auto;
}

.addon-product-actions {
  padding: 12px 16px; /* Increased padding */
  background: linear-gradient(to top, #f7fafc, #fff);
  border-top: 1px solid #e2e8f0;
}

.quantity-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.quantity-btn {
  cursor: pointer;
  width: 32px;
  height: 32px;
  background-color: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.quantity-btn:hover:not(:disabled) {
  background-color: #edf2f7;
  border-color: #cbd5e0;
  transform: scale(1.05);
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-btn svg {
  width: 16px;
  height: 16px;
}

.quantity-display {
  font-size: 16px;
  width: 40px;
  text-align: center;
}

.no-addons-selected,
.no-addons-available {
  text-align: center;
  padding: 20px;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  margin-top: 20px;
}

.no-addons-selected p,
.no-addons-available p {
  margin: 0;
  font-size: 16px;
}

.selected-addons-summary {
  margin-top: 20px;
  padding: 15px;
  background-color: #e9f7ef;
  border: 1px solid #c3e6cb;
  border-radius: 8px;

  h4 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 16px;
    color: #064e3b;
  }
}

.selected-addons-list {
  list-style: none;
  padding: 0;
  margin: 0 0 10px 0;
}

/* Cải thiện căn chỉnh cho các item đã chọn */
.selected-addon-item {
  display: grid;
  grid-template-columns: 1fr auto auto; /* Chia thành 3 cột với tỉ lệ thích hợp */
  align-items: center;
  gap: 10px; /* Khoảng cách giữa các cột */
  padding: 8px 0;
  border-bottom: 1px solid #d1e7dd;
}

.selected-addon-name {
  text-align: left;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-addon-quantity {
  text-align: center;
  padding: 0 10px;
  min-width: 50px; /* Đảm bảo chiều rộng tối thiểu */
}

.selected-addon-price {
  text-align: right;
  font-weight: 600;
  color: #064e3b;
  min-width: 80px; /* Đảm bảo chiều rộng tối thiểu cho phần giá */
}

.addons-total {
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 2px solid #c3e6cb;
  font-weight: bold;
  margin-top: 5px;
}

.booking-summary {
  margin-top: 2rem;
  padding: 1.75rem;
  background: linear-gradient(to bottom right, #f7fafc, #f0f5fa);
  border-radius: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.8);

  .selected-info {
    text-align: left;

    .route {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #1a202c;
    }

    .time,
    .seats,
    .ticket-type {
      color: #075985;
      font-size: 0.925rem;
      margin-bottom: 0.375rem;
    }
  }

  .total-price {
    text-align: right;

    .amount {
      display: block;
      font-size: 1.625rem;
      font-weight: 600;
      color: #15803d;
      margin-bottom: 0.5rem;
      text-shadow: 0 1px 2px rgba(56, 161, 105, 0.1);
    }
  }
}

.selected-info {
  margin-bottom: 10px;
}

.route {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 5px 0;
}

.time,
.ticket-type,
.seats,
.group-info,
.addons-info {
  font-size: 14px;
  margin: 0 0 5px 0;
}

.total-price {
  text-align: right;
  font-size: 18px;
  font-weight: bold;
}

.addons-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0;
  font-style: italic;
  padding: 0.5rem 0;
}

/* Image count badge */
.image-count-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Product detail modal */
.product-detail-modal {
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.product-detail-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.main-image img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

.image-thumbnails {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  overflow-x: auto;
}

.thumbnail {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
}

.thumbnail.active {
  border-color: #3b82f6;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info h3 {
  margin: 0 0 12px 0;
  font-size: 1.5rem;
  color: #1f2937;
}

.product-info .price {
  font-size: 1.25rem;
  font-weight: 600;
  color: #059669;
  margin-bottom: 16px;
}

.product-info .description {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 20px;
}

.quantity-section {
  margin-top: 24px;
}

.quantity-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #f3f4f6;
}

.modal-body {
  padding: 24px;
}

/* Responsive */
@media (max-width: 768px) {
  .product-detail-content {
    grid-template-columns: 1fr;
  }

  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .main-image img {
    height: 250px;
  }

  /* Mobile addon carousel layout */
  .addon-products-grid {
    /* Recycle flex layout but adjustments for mobile */
    padding-bottom: 8px;
    scroll-snap-type: x mandatory;
    scrollbar-width: none; /* Firefox */
  }

  .addon-products-grid::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  .addon-product-card {
    flex: 0 0 calc(33.333% - 8px);
    min-width: 110px;
    max-width: 150px;
    scroll-snap-align: start;
    height: auto;
  }

  .addon-product-image {
    height: 80px;
  }

  .product-image {
    height: 80px;
  }

  .image-placeholder {
    svg {
      width: 24px;
      height: 24px;
      margin-bottom: 0.25rem;
    }

    span {
      font-size: 0.65rem;
      padding: 0 0.25rem;
    }
  }

  .addon-product-info {
    padding: 8px;
  }

  .addon-product-name {
    font-size: 0.75rem;
    margin: 0 0 4px 0;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Hide description on mobile */
  .addon-product-description {
    display: none;
  }

  /* Hide stock info details on mobile - just show in/out indicator */
  .stock-info {
    display: none;
  }

  .addon-product-price {
    font-size: 0.75rem;
    color: #059669;
  }

  .addon-product-actions {
    padding: 6px 8px;
  }

  .quantity-controls {
    gap: 4px;
  }

  .quantity-btn {
    width: 24px;
    height: 24px;
  }

  .quantity-btn svg {
    width: 12px;
    height: 12px;
  }

  .quantity-display {
    font-size: 0.8rem;
    width: 24px;
  }

  .out-of-stock-message {
    font-size: 0.65rem;
    padding: 6px;
  }

  /* Hide image count badge on mobile */
  .image-count-badge {
    display: none;
  }

  /* Compact section title */
  .section-title {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  .addons-subtitle {
    font-size: 0.8rem;
    padding: 0.25rem 0;
  }

  /* Compact booking summary */
  .booking-summary {
    padding: 1rem;
    margin-top: 1rem;
  }

  .selected-addons-summary {
    padding: 10px;
    margin-top: 12px;
  }

  .selected-addons-summary h4 {
    font-size: 0.875rem;
    margin-bottom: 8px;
  }

  .selected-addon-item {
    padding: 4px 0;
    font-size: 0.8rem;
  }
}

.addon-product-clickable {
  cursor: pointer;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.addon-product-actions {
  padding: 15px;
  background-color: #f1f1f1;
  border-top: 1px solid #e1e1e1;
  /* Loại bỏ cursor pointer để chỉ actions có cursor riêng */
}

.out-of-stock-message {
  text-align: center;
  padding: 12px;
  background-color: #f3f4f6;
  border-radius: 6px;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
